import { useAppSelector } from "integrations/redux/hooks"
import { compareFunctionsBySortMode } from "./constants"
import {
    selectFilterLayersCreatorId,
    selectFilterLayersValue,
    selectSearchLayersValue,
    selectSortLayersValue,
} from "./slice.new"
import { getSidePanelStickyLayersPlugins } from "plugins/plugin-registry"

export type SidePanelStickyLayersData = {
    id: string
    index: string
    rowKey: string
    plugin: ReturnType<typeof getSidePanelStickyLayersPlugins>[number]
}

const useStickyLayerSidePanelData = () => {
    const sidePanelPlugins = getSidePanelStickyLayersPlugins()
    const data: Record<string, SidePanelStickyLayersData> = sidePanelPlugins.reduce(
        (acc, cur, pluginIndex) => {
            const entities = cur.useSidePanelEntities()
            entities.forEach((entity) => {
                acc[entity.id] = { ...entity, rowKey: `${pluginIndex}-${entity.id}`, plugin: cur }
            })
            return acc
        },
        {} as Record<string, SidePanelStickyLayersData>
    )
    const roots = Object.values(data).map((entity) => entity)
    return roots
}

const useStickyLayerSidePanelEntityMap = () => {
    const sidePanelData = useStickyLayerSidePanelData()
    const entityMaps = getSidePanelStickyLayersPlugins().map((plugin) =>
        plugin.useSidePanelEntityMap(
            sidePanelData.filter((data) => data.plugin === plugin).map(({ id }) => id)
        )
    )
    return Object.fromEntries(entityMaps.flatMap(Object.entries))
}

export const useFilteredAndSortedStickyLayerSidePanelData = () => {
    const sidePanelData = useStickyLayerSidePanelData()
    const searchLayersValue = useAppSelector(selectSearchLayersValue)
    const filterLayersValue = useAppSelector(selectFilterLayersValue)
    const filterLayerCreatorId = useAppSelector(selectFilterLayersCreatorId)
    const sortLayersValue = useAppSelector(selectSortLayersValue)
    const entityMap = useStickyLayerSidePanelEntityMap()
    const filterList = (list: SidePanelStickyLayersData[]) => {
        return list
            .map((data): SidePanelStickyLayersData | null => {
                const entity = entityMap[data.id]
                const matchesSearch = entity.name
                    .toLowerCase()
                    .includes(searchLayersValue.toLocaleLowerCase())
                const matchesFilter =
                    !filterLayersValue.length ||
                    !data.plugin.filterKey ||
                    filterLayersValue.includes(data.plugin.filterKey)
                const matchesCreator =
                    !filterLayerCreatorId || entity.createdBy?.userId === filterLayerCreatorId
                const matches = matchesSearch && matchesFilter && matchesCreator
                if (matches) {
                    return data
                }
                return null
            })
            .filter((x): x is SidePanelStickyLayersData => x !== null)
    }
    const filteredList = filterList(sidePanelData)
    const compareFn = compareFunctionsBySortMode[sortLayersValue]
    const sortArr = (arr: SidePanelStickyLayersData[]) => {
        arr.sort((a, b) => {
            const entityA = entityMap[a.id]
            const entityB = entityMap[b.id]
            return compareFn(entityA, entityB)
        })
    }
    sortArr(filteredList)
    return filteredList
}

export const useStickyLayerSidePanelSortedEntities = () => {
    const layerSidePanelData = useFilteredAndSortedStickyLayerSidePanelData()
    const entityMap = useStickyLayerSidePanelEntityMap()
    return layerSidePanelData.map((layer) => entityMap[layer.id])
}
